import { useMemo, useState } from "react";
import Select from "react-select";
import { Eye, EyeOff, Search, X } from "lucide-react";

import axiosInstance from "../../../../redux/api";
import { usePermissions } from "../../../../Hooks/usePermissions";
import { MENU, PERMISSION } from "../../../../lib/permissions";
import { Button } from "Components/ui/button";
import { Checkbox } from "Components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "Components/ui/dialog";
import { cn } from "lib/utils";

const SIZE_CLASSES = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-2xl",
  "2xl": "max-w-3xl",
  "3xl": "max-w-4xl",
};

/**
 * Thin Dialog wrapper that bakes in the title/subtitle + size + footer pattern
 * shared by every Pending Products modal. The hand-rolled `Modal` shell this
 * replaces was reimplementing focus trap, scroll lock, click-outside-to-close
 * and Escape handling — Radix Dialog gives us all of that for free.
 */
function PendingProductsDialog({ open, onClose, title, description, size = "md", children, footer }) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        className={cn(
          "p-0 gap-0 max-h-[90vh] flex flex-col w-full",
          SIZE_CLASSES[size] || SIZE_CLASSES.md
        )}
      >
        <DialogHeader className="px-5 py-4 border-b">
          <DialogTitle className="text-base">{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="flex-1 overflow-auto px-5 py-4">{children}</div>
        {footer && (
          <DialogFooter className="px-5 py-3 border-t">{footer}</DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ========== Column Visibility Modal ==========
const COLUMN_GROUPS = [
  {
    key: "identity",
    label: "Identity",
    keys: ["barcode", "name", "ar_name", "description", "ar_description"],
  },
  {
    key: "taxonomy",
    label: "Taxonomy",
    keys: [
      "brand",
      "category",
      "ar_category",
      "sub_category",
      "ar_sub_category",
      "type",
      "ar_type",
      "occasion",
    ],
  },
  {
    key: "pricing",
    label: "Pricing & inventory",
    keys: ["is_b2b", "cost", "regular_price", "sale_price", "quantity"],
  },
  {
    key: "media",
    label: "Media",
    keys: ["image", "reference_link"],
  },
  {
    key: "variant",
    label: "Variant attributes",
    keys: ["product_option_type", "color", "size", "height", "width", "position"],
  },
  {
    key: "schedule",
    label: "Status & schedule",
    keys: ["new_from", "new_to", "status"],
  },
];

const REQUIRED_COLUMN_KEYS = new Set([
  "name",
  "ar_name",
  "description",
  "ar_description",
  "quantity",
  "cost",
  "regular_price",
  "sale_price",
]);

function ColumnSwitch({ checked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      className={cn(
        "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border border-transparent transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
        checked ? "bg-primary" : "bg-muted"
      )}
    >
      <span
        className={cn(
          "inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition-transform",
          checked ? "translate-x-[18px]" : "translate-x-[2px]"
        )}
      />
    </button>
  );
}

export const ColumnVisibilityModal = ({
  columns,
  visibleColumns,
  onToggle,
  onClose,
}) => {
  const [query, setQuery] = useState("");

  const columnByKey = useMemo(() => {
    const m = new Map();
    columns.forEach((c) => m.set(c.key, c));
    return m;
  }, [columns]);

  // Bucket every column into a group; anything we forgot to map ends up in "Other"
  // so we never silently hide columns from the modal.
  const groups = useMemo(() => {
    const claimed = new Set();
    const built = COLUMN_GROUPS.map((g) => {
      const cols = g.keys.map((k) => columnByKey.get(k)).filter(Boolean);
      cols.forEach((c) => claimed.add(c.key));
      return { ...g, columns: cols };
    });
    const leftover = columns.filter((c) => !claimed.has(c.key));
    if (leftover.length > 0) {
      built.push({ key: "other", label: "Other", columns: leftover });
    }
    return built;
  }, [columns, columnByKey]);

  const q = query.trim().toLowerCase();
  const filteredGroups = q
    ? groups
        .map((g) => ({
          ...g,
          columns: g.columns.filter((c) => c.label.toLowerCase().includes(q)),
        }))
        .filter((g) => g.columns.length > 0)
    : groups;

  const visibleCount = visibleColumns.size;
  const totalCount = columns.length;

  const showAll = () => {
    columns.forEach((c) => {
      if (!visibleColumns.has(c.key)) onToggle(c.key);
    });
  };
  const hideAll = () => {
    columns.forEach((c) => {
      if (visibleColumns.has(c.key) && !REQUIRED_COLUMN_KEYS.has(c.key)) {
        onToggle(c.key);
      }
    });
  };

  return (
    <PendingProductsDialog
      open
      onClose={onClose}
      title="Column visibility"
      description={`Showing ${visibleCount} of ${totalCount} columns. Required fields stay visible at all times.`}
      size="2xl"
      footer={
        <>
          <div className="mr-auto flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={showAll}
              className="h-8 gap-1.5 text-xs"
            >
              <Eye className="h-3.5 w-3.5" />
              Show all
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={hideAll}
              className="h-8 gap-1.5 text-xs"
            >
              <EyeOff className="h-3.5 w-3.5" />
              Hide optional
            </Button>
          </div>
          <Button onClick={onClose}>Done</Button>
        </>
      }
    >
      <div className="sticky top-0 z-[1] -mx-5 -mt-4 mb-4 border-b bg-background/95 px-5 py-3 backdrop-blur">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search columns…"
            className="h-9 w-full rounded-md border border-input bg-background pl-9 pr-9 text-sm outline-none transition-colors focus:border-ring focus:ring-2 focus:ring-ring focus:ring-offset-1"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm border-0 bg-transparent p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="space-y-5">
        {filteredGroups.length === 0 && (
          <div className="rounded-lg border border-dashed py-10 text-center text-sm text-muted-foreground">
            No columns match "{query}".
          </div>
        )}

        {filteredGroups.map((group) => {
          const groupVisible = group.columns.filter((c) =>
            visibleColumns.has(c.key)
          ).length;
          return (
            <section key={group.key}>
              <header className="mb-2 flex items-center justify-between">
                <h4 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {group.label}
                </h4>
                <span className="text-[11px] text-muted-foreground">
                  {groupVisible}/{group.columns.length}
                </span>
              </header>
              <div className="grid grid-cols-1 gap-1.5 rounded-lg border bg-muted/20 p-1.5 sm:grid-cols-2">
                {group.columns.map((column) => {
                  const isOn = visibleColumns.has(column.key);
                  const isRequired = REQUIRED_COLUMN_KEYS.has(column.key);
                  return (
                    <label
                      key={column.key}
                      className={cn(
                        "flex cursor-pointer items-center justify-between gap-3 rounded-md border bg-background px-3 py-2 transition-colors",
                        isOn
                          ? "border-primary/30 shadow-sm"
                          : "border-transparent hover:border-border hover:bg-card"
                      )}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span
                            className={cn(
                              "truncate text-sm",
                              isOn ? "font-medium text-foreground" : "text-muted-foreground"
                            )}
                          >
                            {column.label}
                          </span>
                          {isRequired && (
                            <span className="rounded bg-destructive/10 px-1 py-px text-[9px] font-semibold uppercase tracking-wider text-destructive">
                              Req
                            </span>
                          )}
                        </div>
                      </div>
                      <ColumnSwitch
                        checked={isOn}
                        onChange={() => onToggle(column.key)}
                      />
                    </label>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </PendingProductsDialog>
  );
};

// ========== Bulk Edit Modal ==========
const BULK_EDIT_FIELDS = [
  { key: "name",                label: "Name",                type: "text" },
  { key: "ar_name",             label: "Arabic Name",         type: "text" },
  { key: "barcode",             label: "Barcode",             type: "text" },
  { key: "brand",               label: "Brand",               type: "text" },
  { key: "type",                label: "Type",                type: "dynamic-select" },
  { key: "ar_type",             label: "Arabic Type",         type: "text" },
  { key: "status",              label: "Status",              type: "select", options: [{ value: "Active", label: "Active" }, { value: "Inactive", label: "Inactive" }] },
  { key: "description",         label: "Description",         type: "text" },
  { key: "ar_description",      label: "Arabic Description",  type: "text" },
  { key: "category",            label: "Category",            type: "text" },
  { key: "ar_category",         label: "Arabic Category",     type: "text" },
  { key: "sub_category",        label: "Sub Category",        type: "dynamic-select" },
  { key: "ar_sub_category",     label: "Arabic Sub Category", type: "text" },
  { key: "cost",                label: "Cost Price",          type: "number" },
  { key: "regular_price",       label: "Regular Price",       type: "number" },
  { key: "sale_price",          label: "Sale Price",          type: "number" },
  { key: "is_b2b",              label: "B2B",                 type: "select", options: [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }] },
  { key: "quantity",            label: "Quantity",            type: "number" },
  { key: "color",               label: "Color",               type: "text" },
  { key: "size",                label: "Size",                type: "text" },
  { key: "product_option_type", label: "Product Option Type", type: "select", options: [{ value: "Product", label: "Product" }, { value: "Varient", label: "Varient" }] },
  { key: "height",              label: "Height",              type: "number" },
  { key: "width",               label: "Width",               type: "number" },
  { key: "position",            label: "Position",            type: "number" },
  { key: "new_from",            label: "New From",            type: "date" },
  { key: "new_to",              label: "New To",              type: "date" },
];

const bulkInputClass =
  "w-full h-8 px-2 text-xs rounded-md border border-input bg-background text-foreground shadow-sm transition-colors " +
  "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 focus:border-ring " +
  "disabled:bg-muted disabled:cursor-not-allowed disabled:opacity-60";

export const BulkEditModal = ({
  bulkEditValues,
  setBulkEditValues,
  onApply,
  onClose,
  typeOptions = [],
  subCategoryOptions = [],
}) => {
  const { hasAction } = usePermissions();
  const canEditCost = hasAction(MENU.PRODUCT_COSTS, PERMISSION.EDIT);
  const canEditRegularPrice = hasAction(
    MENU.PRODUCT_REGULAR_PRICE,
    PERMISSION.EDIT,
  );
  const canEditSalePrice = hasAction(MENU.PRODUCT_SALE_PRICE, PERMISSION.EDIT);

  // Bulk-edit only exposes a price field if the caller has EDIT — otherwise
  // the backend would 422 the eventual bulk-sync.
  const editableFields = useMemo(() => {
    const drop = new Set();
    if (!canEditCost) drop.add("cost");
    if (!canEditRegularPrice) drop.add("regular_price");
    if (!canEditSalePrice) drop.add("sale_price");
    return BULK_EDIT_FIELDS.filter((f) => !drop.has(f.key));
  }, [canEditCost, canEditRegularPrice, canEditSalePrice]);

  const dynamicOptionsMap = {
    type: typeOptions,
    sub_category: subCategoryOptions,
  };

  const toggle = (key, checked) => {
    setBulkEditValues((prev) => {
      const updated = { ...prev };
      if (checked) updated[key] = "";
      else delete updated[key];
      return updated;
    });
  };

  const update = (key, value) => {
    setBulkEditValues((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <PendingProductsDialog
      open
      onClose={onClose}
      title="Bulk edit"
      description="Check a field to enable it — only checked fields will be applied to the selected rows."
      size="3xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={onApply}>Apply</Button>
        </>
      }
    >
      <table className="w-full text-xs border border-border border-collapse">
        <thead className="bg-muted sticky top-0 z-[1]">
          <tr>
            <th className="w-9 px-2 py-2 border-b border-border" />
            <th className="w-40 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b border-border">
              Field
            </th>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b border-border">
              Value
            </th>
          </tr>
        </thead>
        <tbody>
          {editableFields.map(({ key, label, type, options }) => {
            const isEnabled = key in bulkEditValues;
            const value = bulkEditValues[key] ?? "";
            return (
              <tr
                key={key}
                className={cn(
                  "border-b border-muted",
                  isEnabled ? "bg-amber-50" : "bg-card"
                )}
              >
                <td className="text-center align-middle px-2 py-2">
                  <Checkbox
                    id={`bulk-${key}`}
                    checked={isEnabled}
                    onCheckedChange={(checked) => toggle(key, checked === true)}
                  />
                </td>
                <td className="align-middle px-3 py-2">
                  <label
                    htmlFor={`bulk-${key}`}
                    className={cn(
                      "cursor-pointer text-sm m-0",
                      isEnabled
                        ? "font-semibold text-foreground"
                        : "font-normal text-muted-foreground"
                    )}
                  >
                    {label}
                  </label>
                </td>
                <td className="align-middle px-3 py-2">
                  {type === "select" || type === "dynamic-select" ? (
                    <select
                      className={bulkInputClass}
                      value={value}
                      disabled={!isEnabled}
                      onChange={(e) => update(key, e.target.value)}
                    >
                      <option value="">— Select —</option>
                      {(type === "dynamic-select"
                        ? dynamicOptionsMap[key] || []
                        : options
                      ).map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={type}
                      className={bulkInputClass}
                      value={value}
                      disabled={!isEnabled}
                      placeholder={isEnabled ? `Enter ${label}` : ""}
                      onChange={(e) => update(key, e.target.value)}
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </PendingProductsDialog>
  );
};

// ========== Category/Subcategory Modal ==========
export const CategorySubcategoryModal = ({
  modalValue,
  setModalValue,
  categoryOptions,
  subCategories,
  setSubCategories,
  onDone,
  onClose,
}) => {
  const handleCategoryChange = async (selected) => {
    setModalValue((prev) => ({ ...prev, category: selected }));

    if (selected.length === 0) {
      setModalValue((prev) => ({ ...prev, sub_category: [] }));
      setSubCategories([]);
      return;
    }

    try {
      const ids = selected.map((opt) => opt.id).join(",");
      const res = await axiosInstance.get("admin/sub-categories", {
        params: { category_ids: ids, page: 1, search_key: "", status: "" },
      });
      const list = res.data.response || [];
      const options = list.map((item) => ({
        value: item.title,
        label: item.title,
      }));
      setSubCategories(options);
    } catch (err) {
      console.error("Failed to fetch subcategories:", err);
      setSubCategories([]);
    }
  };

  return (
    <PendingProductsDialog
      open
      onClose={onClose}
      title="Select category & sub-category"
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={onDone}>Done</Button>
        </>
      }
    >
      <div className="mb-4">
        <h6 className="text-sm font-semibold text-foreground mb-2">
          Select Category
        </h6>
        <Select
          isMulti
          value={modalValue.category}
          options={categoryOptions}
          onChange={handleCategoryChange}
        />
      </div>
      <div>
        <h6 className="text-sm font-semibold text-foreground mb-2">
          Select Sub Category
        </h6>
        <Select
          isMulti
          value={modalValue.sub_category}
          options={subCategories}
          onChange={(selected) =>
            setModalValue((prev) => ({ ...prev, sub_category: selected }))
          }
        />
      </div>
    </PendingProductsDialog>
  );
};

// ========== Confirm Submit Modal ==========
export const ConfirmSubmitModal = ({ selectedCount, onConfirm, onClose }) => (
  <PendingProductsDialog
    open
    onClose={onClose}
    title="Confirm save"
    size="md"
    footer={
      <>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={onConfirm}>Yes, save products</Button>
      </>
    }
  >
    <p className="text-sm text-foreground mb-2">
      You are about to save{" "}
      <strong className="font-semibold">
        {selectedCount} product{selectedCount !== 1 ? "s" : ""}
      </strong>{" "}
      to the Product List. This action cannot be undone.
    </p>
    <p className="text-xs text-muted-foreground">
      Unselected rows will remain in the temp table for future review.
    </p>
  </PendingProductsDialog>
);

// ========== Delete Confirmation Modal ==========
export const DeleteConfirmModal = ({ count, onConfirm, onClose }) => (
  <PendingProductsDialog
    open
    onClose={onClose}
    title="Confirm delete"
    size="md"
    footer={
      <>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button variant="destructive" onClick={onConfirm}>Yes, delete</Button>
      </>
    }
  >
    <p className="text-sm text-foreground">
      Are you sure you want to delete{" "}
      <strong className="font-semibold">
        {count} row{count !== 1 ? "s" : ""}
      </strong>
      ? This will remove them from the temp table.
    </p>
  </PendingProductsDialog>
);
